import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { EnterpriseSidebar } from "./EnterpriseDashboard";
import { useCyclePicker, CyclePickerBar } from "../hooks/useCyclePicker";

const TAB_STYLES = {
    Retain:   { accent: "text-emerald-300", border: "border-emerald-500/40", pill: "bg-emerald-500/10" },
    Retrain:  { accent: "text-amber-300",   border: "border-amber-500/40",   pill: "bg-amber-500/10" },
    Redeploy: { accent: "text-red-300",     border: "border-red-500/40",     pill: "bg-red-500/10" },
    Unassigned:{ accent: "text-gray-400",   border: "border-border/40",      pill: "bg-black/40" },
};

const TAB_COPY = {
    Retain:   "Preserve and invest. Strong capability, low displacement risk.",
    Retrain:  "Close the gap. Strategic training pathways pending.",
    Redeploy: "Reposition. High displacement risk requires new deployment.",
    Unassigned: "No verdict yet — run scoring and displacement to assign.",
};

export default function CohortManagement() {
    const { api } = useAuth();
    const navigate = useNavigate();
    const picker = useCyclePicker();
    const { cycleId } = picker;
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [activeTab, setActiveTab] = useState("Retain");

    useEffect(() => {
        if (!cycleId) return;
        let cancelled = false;
        setLoading(true);
        api.get(`/v2/dashboard/cohorts/${cycleId}`)
            .then((r) => { if (!cancelled) setData(r.data); })
            .catch((e) => { console.error(e); })
            .finally(() => { if (!cancelled) setLoading(false); });
        return () => { cancelled = true; };
    }, [api, cycleId]);

    const cohorts = data?.cohorts || {};
    const summary = data?.summary || {};
    const list = cohorts[activeTab] || [];

    return (
        <div className="flex">
            <EnterpriseSidebar current="/enterprise/cohorts" />
            <main className="ml-56 flex-1 p-8 min-h-screen" data-testid="cohort-management">
                <div className="max-w-6xl space-y-6">
                    <div>
                        <h1 className="text-2xl font-display font-bold text-white">Cohorts</h1>
                        <p className="text-sm text-gray-500 mt-1 font-body">Verdict-segmented talent lists from the latest scoring cycle</p>
                    </div>

                    <CyclePickerBar picker={picker} />

                    {/* Summary tiles */}
                    <div className="grid grid-cols-2 lg:grid-cols-4 gap-4" data-testid="cohort-summary">
                        {["Retain", "Retrain", "Redeploy", "Unassigned"].map((k) => {
                            const s = TAB_STYLES[k];
                            return (
                                <div key={k} className={`p-4 border ${s.border} ${s.pill}`}>
                                    <p className={`text-xs uppercase tracking-wider ${s.accent}`}>{k}</p>
                                    <p className={`text-2xl font-mono font-bold mt-1 ${s.accent}`}>{summary[k] ?? 0}</p>
                                </div>
                            );
                        })}
                    </div>

                    {/* Tabs */}
                    <div className="flex items-center gap-2 border-b border-border/40 text-sm">
                        {["Retain", "Retrain", "Redeploy", "Unassigned"].map((k) => {
                            const s = TAB_STYLES[k];
                            const active = activeTab === k;
                            return (
                                <button key={k} onClick={() => setActiveTab(k)}
                                    className={`px-4 py-2 -mb-px border-b-2 transition-colors ${active ? `${s.accent} ${s.border}` : "text-gray-500 border-transparent hover:text-white"}`}
                                    data-testid={`tab-${k.toLowerCase()}`}>
                                    {k} · {summary[k] ?? 0}
                                </button>
                            );
                        })}
                    </div>

                    <p className="text-xs text-gray-500 -mt-3">{TAB_COPY[activeTab]}</p>

                    {loading && <p className="text-xs text-gray-500">Loading…</p>}

                    {/* Candidate list */}
                    <div className="bg-card border border-border/40" data-testid="cohort-list">
                        <table className="w-full text-xs">
                            <thead>
                                <tr className="text-gray-500 uppercase tracking-wider border-b border-border/40">
                                    <th className="text-left px-4 py-2">Name</th>
                                    <th className="text-left px-4 py-2">Role</th>
                                    <th className="text-left px-4 py-2">Cadre</th>
                                    <th className="text-left px-4 py-2">Segment</th>
                                    <th className="text-right px-4 py-2">Capability</th>
                                    <th className="text-right px-4 py-2">Displacement</th>
                                    <th className="text-left px-4 py-2">Flags</th>
                                </tr>
                            </thead>
                            <tbody>
                                {list.map((r) => (
                                    <tr key={r.candidate_id}
                                        onClick={() => navigate(`/enterprise/worker/${r.candidate_id}`)}
                                        className="border-b border-border/20 hover:bg-white/5 cursor-pointer">
                                        <td className="px-4 py-2 text-white">{r.full_name}</td>
                                        <td className="px-4 py-2 text-gray-400">{r.job_title}</td>
                                        <td className="px-4 py-2 text-gray-500">{r.cadre || "—"}</td>
                                        <td className="px-4 py-2 text-gray-400">{r.capability_segment || "—"}</td>
                                        <td className="px-4 py-2 text-right font-mono text-white">{r.capability_composite !== null ? r.capability_composite.toFixed(1) : "—"}</td>
                                        <td className="px-4 py-2 text-right font-mono text-white">{r.displacement_composite !== null ? r.displacement_composite.toFixed(1) : "—"}</td>
                                        <td className="px-4 py-2">
                                            <div className="flex flex-wrap gap-1">
                                                {(r.triggered_flags || []).map((f) => (
                                                    <span key={f} className="text-xs px-1.5 py-0.5 border border-amber-500/30 text-amber-300 font-mono">{f}</span>
                                                ))}
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                                {list.length === 0 && !loading && (
                                    <tr><td colSpan="7" className="px-4 py-6 text-center text-gray-600">No candidates in this cohort.</td></tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
            </main>
        </div>
    );
}
